import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";

import {useGetPianAttDTO, useGetProgettiPianAtt,} from "./Services";
import {useGetDropProgettiByCodDip} from "../../common/Services";
import {Progetti, ProgettiApiResponse} from "../gestversore/types";


dayjs.extend(minMax);

type DipendenteOption = {
  coddip: string;
  nomedip: string;
};


const getColorByPercomp = (percomp: number) => {
  if (percomp > 100) return "#e53935"; // Ritardo
  if (percomp === 100) return "#808080"; // Terminata
  if (percomp > 0) return "#43a047"; // In corso
  return "#1e90ff"; // Pianificata
};

const GanttPianAtt: React.FC = () => {
  const isIframe = window.self !== window.top;

  const [globalFilter] = useState("");
  const [sorting] = useState<MRT_SortingState>([]);

  const [pagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 1000000,
  });

const [selectedProgetto, setSelectedProgetto] = useState<Progetti | null>(null);

  const [selectedDipendente, setSelectedDipendente] = useState<string[]>([]);

const [visibleDate, setVisibleDate] = useState(dayjs());
const [ganttView, setGanttView] = useState<"month" | "week">("month");


const keycoddip = localStorage.getItem("keycoddip") || "";
const keyrole = localStorage.getItem("keyrole") || "";
const isAdmin =
  keyrole.includes('TimeReportAdmin') ||
  keyrole.includes('TimeReportPianAtt');

 let filterProgs = '';
 if (isAdmin) {  filterProgs = 'addAdminProgs';} else {  filterProgs = 'addRefProgs';}




 const columnFilters = useMemo<MRT_ColumnFiltersState>(() => {
  if (!selectedProgetto?.idprog) {
    return [];
  }

  return [
    {
      id: "IDPROG",
      value: selectedProgetto.idprog,
    },
  ];
}, [selectedProgetto]);

const {
  data: { results = [] } = {},
  isLoading: isLoadingAttivita,
  isFetching: isFetchingAttivita,
} = useGetPianAttDTO(
  pagination,
  columnFilters,
  globalFilter,
  sorting,
  {
    enabled: Boolean(selectedProgetto?.idprog),
  }
);

const { data: ProgettiApiResponse, isLoading: isLoadingProgetti } =  useGetDropProgettiByCodDip();

const progettoOptions: Progetti[] = useMemo(() => {  return ProgettiApiResponse?.results ?? [];}, [ProgettiApiResponse]);



  const dipendenteOptions = useMemo<DipendenteOption[]>(() => {
    const map = new Map<string, DipendenteOption>();

    results.forEach((r: any) => {
      if (r.coddip && r.nomedip) {
        map.set(r.coddip, {
          coddip: r.coddip,
          nomedip: r.nomedip,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.nomedip.localeCompare(b.nomedip, undefined, {
        sensitivity: "base",
      })
    );
  }, [results]);

  /*
   * Filtro risorse lato frontend.
   * Il progetto è già filtrato lato query/backend.
   */
  const filteredActivities = useMemo(() => {
    return results.filter((item: any) => {
      if (
        selectedDipendente.length > 0 &&
        !selectedDipendente.includes(item.coddip)
      ) {
        return false;
      }

      return Boolean(item.datainiziopian && item.datafinepian);
    });
  }, [results, selectedDipendente]);

  /*
   * Intervallo complessivo del Gantt.
   */
const ganttRange = useMemo(() => {
  if (ganttView === "week") {
    const start = visibleDate.startOf("week").add(1, "day");

    return {
      start,
      end: start.add(6, "day"),
    };
  }

  return {
    start: visibleDate.startOf("month"),
    end: visibleDate.endOf("month"),
  };
}, [visibleDate, ganttView]);

  /*
   * Giorni visualizzati in orizzontale.
   */
  const days = useMemo(() => {
    const list: dayjs.Dayjs[] = [];

    let current = ganttRange.start.startOf("day");

    while (
      current.isBefore(ganttRange.end, "day") ||
      current.isSame(ganttRange.end, "day")
    ) {
      list.push(current);
      current = current.add(1, "day");
    }

    return list;
  }, [ganttRange]);

  const visibleActivities = useMemo(() => {
      return filteredActivities.filter((item: any) => {
        const start = dayjs(item.datainiziopian);
        const end = dayjs(item.datafinepian);

        return (
          (start.isBefore(ganttRange.end, "day") ||
            start.isSame(ganttRange.end, "day")) &&
          (end.isAfter(ganttRange.start, "day") ||
            end.isSame(ganttRange.start, "day"))
        );
      });
    }, [filteredActivities, ganttRange]);

  const groupedByNodo = useMemo(() => {
        const map = new Map<string, any[]>();

        visibleActivities.forEach((item: any) => {
          const nodo = item.desnodo || "Senza nodo";

          if (!map.has(nodo)) {
            map.set(nodo, []);
          }
          map.get(nodo)!.push(item);
        });

        return Array.from(map.entries()).map(([nodo, activities]) => ({
          nodo,
          activities: activities.sort((a, b) =>
            dayjs(a.datainiziopian).diff(dayjs(b.datainiziopian))
          ),
        }));
      }, [visibleActivities]);


const getLeftPx = (date: string) => {
  const safeDate = dayjs(date).isBefore(ganttRange.start, "day")
    ? ganttRange.start
    : dayjs(date);

  const diffDays = safeDate.diff(ganttRange.start, "day");

  return labelColumnWidth + diffDays * dayColumnWidth;
};

const getWidthPx = (startDate: string, endDate: string) => {
  const safeStart = dayjs(startDate).isBefore(ganttRange.start, "day")
    ? ganttRange.start
    : dayjs(startDate);

  const safeEnd = dayjs(endDate).isAfter(ganttRange.end, "day")
    ? ganttRange.end
    : dayjs(endDate);

  const durationDays = safeEnd.diff(safeStart, "day") + 1;

  return Math.max(durationDays * dayColumnWidth, 20);
};
  const dayColumnWidth = 42;
  const labelColumnWidth = 340;
  const minGridWidth = labelColumnWidth + days.length * dayColumnWidth;

  const showLoading =
    selectedProgetto && (isLoadingAttivita || isFetchingAttivita);

 const showEmpty =
  selectedProgetto &&
  !isLoadingAttivita &&
  !isFetchingAttivita &&
  visibleActivities.length === 0;

const showGantt =
  selectedProgetto &&
  !isLoadingAttivita &&
  !isFetchingAttivita &&
  visibleActivities.length > 0;

  return (
    <Box
      sx={{
        mt: "60px",
        height: isIframe ? "100vh" : "calc(100vh - 64px)",
        boxSizing: "border-box",
        backgroundColor: isIframe ? "#fff" : undefined,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 2,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
     <Typography
  variant="h6"
  fontWeight={600}
  sx={{ textTransform: "uppercase", mr: 2, minWidth: 180 }}
>
  {ganttView === "month"
    ? visibleDate.format("MMMM YYYY")
    : `${ganttRange.start.format("DD/MM/YYYY")} - ${ganttRange.end.format(
        "DD/MM/YYYY"
      )}`}
</Typography>



      <Autocomplete<Progetti>
                size="small"
                loading={isLoadingProgetti}
                options={progettoOptions}
                getOptionLabel={(option) =>
                  option?.label ||
                  option?.desprog ||
                  option?.desprogblocco ||
                  ""
                }
                isOptionEqualToValue={(opt, val) => {
                  if (opt.idprog && val.idprog) {
                    return opt.idprog === val.idprog;
                  }

                  if (opt.codprog && val.codprog) {
                    return opt.codprog === val.codprog;
                  }

                  return (opt.desprog ?? "") === (val.desprog ?? "");
                }}
                value={selectedProgetto}
                onChange={(_, value) => {
                  setSelectedProgetto(value);
                  setSelectedDipendente([]);
                }}
                sx={{ minWidth: 360 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Progetto"
                    placeholder="Seleziona progetto"
                  />
                )}
              />

        <Autocomplete
          multiple
          size="small"
          disabled={!selectedProgetto || results.length === 0}
          options={dipendenteOptions}
          getOptionLabel={(option) => option.nomedip}
          isOptionEqualToValue={(opt, val) => opt.coddip === val.coddip}
          value={dipendenteOptions.filter((o) =>
            selectedDipendente.includes(o.coddip)
          )}
          onChange={(_, options) =>
            setSelectedDipendente(options.map((o) => o.coddip))
          }
          renderTags={(value, getTagProps) => {
            const visible = value.slice(0, 1);
            const hiddenCount = value.length - visible.length;

            return (
              <>
                {visible.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.coddip}
                    label={option.nomedip}
                    size="small"
                  />
                ))}

                {hiddenCount > 0 && (
                  <Chip label={`+${hiddenCount}`} size="small" />
                )}
              </>
            );
          }}
          sx={{ minWidth: 260 }}
          renderInput={(params) => (
            <TextField {...params} label="Risorse" />
          )}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
  <Button
    size="small"
    variant="contained"
    onClick={() =>
      setVisibleDate((prev) =>
        ganttView === "week"
          ? prev.subtract(1, "week")
          : prev.subtract(1, "month")
      )
    }
  >
    &lt;&lt;
  </Button>

  <Button
    size="small"
    variant="contained"
    onClick={() =>
      setVisibleDate((prev) =>
        ganttView === "week"
          ? prev.add(1, "week")
          : prev.add(1, "month")
      )
    }
  >
    &gt;&gt;
  </Button>

  <Button
    size="small"
    variant="contained"
    onClick={() => setVisibleDate(dayjs())}
  >
    OGGI
  </Button>

  <Button
    size="small"
    variant="contained"
    onClick={() => setGanttView("month")}
  >
    MESE
  </Button>

  <Button
    size="small"
    variant="contained"
    onClick={() => setGanttView("week")}
  >
    Sett
  </Button>
</Box>




        <Typography variant="caption" sx={{ color: "#9E9E9E", ml: 1 }}>
          <span style={{ color: "#1e90ff" }}>■</span> Pianificata&nbsp;
          <span style={{ color: "#43a047" }}>■</span> In corso&nbsp;
          <span style={{ color: "#808080" }}>■</span> Terminata&nbsp;
          <span style={{ color: "#e53935" }}>■</span> Ritardo&nbsp;
        </Typography>
      </Box>

      {/* STATO INIZIALE */}
      {!selectedProgetto && (
        <Box
          sx={{
            flex: 1,
            border: "1px dashed #ccc",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fafafa",
          }}
        >
          <Typography color="text.secondary">
            Seleziona un progetto per caricare il Gantt.
          </Typography>
        </Box>
      )}

      {/* LOADING */}
      {showLoading && (
        <Box
          sx={{
            flex: 1,
            border: "1px solid #ddd",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fff",
            gap: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography color="text.secondary">
            Caricamento attività progetto...
          </Typography>
        </Box>
      )}

      {/* NESSUNA ATTIVITÀ */}
      {showEmpty && (
        <Box
          sx={{
            flex: 1,
            border: "1px solid #ddd",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fff",
          }}
        >
          <Typography color="text.secondary">
            Nessuna attività trovata per il progetto selezionato.
          </Typography>
        </Box>
      )}

      {/* GANTT */}
      {showGantt && (
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 1,
            overflow: "auto",
            flex: 1,
            bgcolor: "#fff",
          }}
        >
          {/* HEADER GIORNI */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `${labelColumnWidth}px repeat(${days.length}, ${dayColumnWidth}px)`,
              minWidth: minGridWidth,
              borderBottom: "1px solid #ddd",
              position: "sticky",
              top: 0,
              zIndex: 4,
              bgcolor: "#f5f5f5",
            }}
          >
            <Box
              sx={{
                p: 1,
                fontWeight: 600,
                borderRight: "1px solid #ddd",
                position: "sticky",
                left: 0,
                zIndex: 5,
                bgcolor: "#f5f5f5",
              }}
            >

            </Box>

            {days.map((day) => {
              const isToday = day.isSame(dayjs(), "day");
              const isWeekend = day.day() === 0 || day.day() === 6;

              return (
                <Box
                  key={day.format("YYYY-MM-DD")}
                  sx={{
                    p: 0.5,
                    textAlign: "center",
                    borderLeft: "1px solid #e0e0e0",
                    fontSize: 11,
                    bgcolor: isToday
                      ? "#fff3cd"
                      : isWeekend
                      ? "#fafafa"
                      : "#f5f5f5",
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  <div>{day.format("DD")}/{day.format("MM")}</div>

                </Box>
              );
            })}
          </Box>

          {groupedByNodo.map((group) => (
            <Box key={group.nodo}>
              {/* RIGA PROGETTO */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `${labelColumnWidth}px repeat(${days.length}, ${dayColumnWidth}px)`,
                  minWidth: minGridWidth,
                  bgcolor: "#eeeeee",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    fontWeight: 700,
                    borderRight: "1px solid #ddd",
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    bgcolor: "#eeeeee",
                  }}
                >
                  {group.nodo}
                </Box>

                <Box
                  sx={{
                    gridColumn: `2 / span ${days.length}`,
                  }}
                />
              </Box>

              {/* RIGHE ATTIVITÀ */}
              {group.activities.map((item: any) => {
                const color = getColorByPercomp(Number(item.percomp) || 0);

                return (
                  <Box
                        key={item.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: `${labelColumnWidth}px repeat(${days.length}, ${dayColumnWidth}px)`,
                          minWidth: minGridWidth,
                          minHeight: 30,
                          borderBottom: "1px solid #eee",
                          position: "relative",
                        }}
                      >
                    {/* COLONNA SINISTRA */}
                    {/*<Box*/}
                    {/*  sx={{*/}
                    {/*    p: 1,*/}
                    {/*    fontSize: 13,*/}
                    {/*    borderRight: "1px solid #ddd",*/}
                    {/*    overflow: "hidden",*/}
                    {/*    textOverflow: "ellipsis",*/}
                    {/*    whiteSpace: "nowrap",*/}
                    {/*    bgcolor: "#fff",*/}
                    {/*    position: "sticky",*/}
                    {/*    left: 0,*/}
                    {/*    zIndex: 3,*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  <Tooltip*/}
                    {/*    title={*/}
                    {/*      <>*/}
                    {/*        <div>*/}
                    {/*          <b>Dipendente:</b>{" "}*/}
                    {/*          {item.nomedip || item.coddip}*/}
                    {/*        </div>*/}
                    {/*        <div>*/}
                    {/*          <b>Progetto:</b> {item.desprog}*/}
                    {/*        </div>*/}
                    {/*        <div>*/}
                    {/*          <b>Nodo:</b> {item.desnodo}*/}
                    {/*        </div>*/}
                    {/*        <div>*/}
                    {/*          <b>Note:</b> {item.note}*/}
                    {/*        </div>*/}
                    {/*      </>*/}
                    {/*    }*/}
                    {/*    arrow*/}
                    {/*  >*/}

                    {/*    <span>*/}
                    {/*      {item.coddip} -{" "}*/}
                    {/*      {item.desnodo || item.note || "Attività"}*/}
                    {/*    </span>*/}
                    {/*  </Tooltip>*/}
                    {/*</Box>*/}

                    <Box
                        sx={{
                          borderRight: "1px solid #ddd",
                          bgcolor: "#fff",
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                        }}
                      />

                    {/* GRIGLIA GIORNI */}
                    {days.map((day) => {
                      const isToday = day.isSame(dayjs(), "day");
                      const isWeekend = day.day() === 0 || day.day() === 6;

                      return (
                        <Box
                          key={day.format("YYYY-MM-DD")}
                          sx={{
                            borderLeft: "1px solid #f0f0f0",
                            bgcolor: isToday
                              ? "#fff8e1"
                              : isWeekend
                              ? "#fafafa"
                              : "#fff",
                          }}
                        />
                      );
                    })}

                    {/* BARRA GANTT */}
                    <Tooltip
                      arrow
                      title={
                        <>
                          <div>
                            <b>Progetto:</b> {item.desprog}
                          </div>
                          <div>
                            <b>Nodo:</b> {item.desnodo}
                          </div>
                          <div>
                            <b>Dipendente:</b>{" "}
                            {item.nomedip || item.coddip}
                          </div>
                          <div>
                            <b>Inizio:</b>{" "}
                            {dayjs(item.datainiziopian).format("DD/MM/YYYY")}
                          </div>
                          <div>
                            <b>Fine:</b>{" "}
                            {dayjs(item.datafinepian).format("DD/MM/YYYY")}
                          </div>
                          <div>
                            <b>Completamento:</b> {item.percomp || 0}%
                          </div>
                          <div>
                            <b>Note:</b> {item.note}
                          </div>
                        </>
                      }
                    >
                    <Box
  sx={{
    position: "absolute",
    left: `${getLeftPx(item.datainiziopian)}px`,
    width: `${getWidthPx(item.datainiziopian, item.datafinepian)}px`,
    top: 5,
    height: 20,
    bgcolor: color,
    borderRadius: "10px",
    color: "#fff",
    px: 1,
    fontSize: 11,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    cursor: "pointer",
    zIndex: 2,
  }}
>
  {item.coddip || "Risorsa non assegnata"}
  {" - "}
  {item.percomp || 0}%
</Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GanttPianAtt;